import { useState } from "react";
import {
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Card,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Collapse,
  UnstyledButton,
  Badge,
} from "@mantine/core";
import {
  IconTrash,
  IconPlus,
  IconChevronDown,
  IconChevronUp,
  IconNote,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import RichTextDiagnosis from "../pathologist-review/RichTextDiagnosis";
import { casesService, CaseNote } from "../../services/cases.service";

interface CaseNotesSectionProps {
  caseId: string | null;
  /** When true, removes the outer Paper wrapper and the internal heading — for use inside a widget that already provides a title */
  embedded?: boolean;
}

export function CaseNotesSection({ caseId, embedded }: CaseNotesSectionProps) {
  const [newNote, setNewNote] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["caseNotes", caseId],
    queryFn: () =>
      caseId ? casesService.getNotes(caseId) : Promise.resolve([]),
    enabled: !!caseId,
  });

  const createNoteMutation = useMutation({
    mutationFn: (noteText: string) =>
      casesService.createNote(caseId!, noteText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseNotes", caseId] });
      setNewNote("");
      setIsAdding(false);
      notifications.show({
        title: "Note Added",
        message: "Case note has been saved",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add note",
        color: "red",
      });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => casesService.deleteNote(caseId!, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseNotes", caseId] });
      notifications.show({
        title: "Note Deleted",
        message: "Case note has been removed",
        color: "blue",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete note",
        color: "red",
      });
    },
  });

  const handleAddNote = () => {
    const plainText = newNote.replace(/<[^>]*>/g, "").trim();
    if (plainText) {
      createNoteMutation.mutate(newNote);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  // Only allow collapsing if there are notes
  const canCollapse = notes.length > 0;
  const effectiveExpanded = canCollapse ? isExpanded : true;

  // Get the latest note for collapsed preview
  const latestNote = notes.length > 0 ? notes[0] : null;

  if (!caseId) {
    if (embedded) {
      return (
        <Text c="dimmed" ta="center" size="sm">
          Save the case first to add notes
        </Text>
      );
    }
    return (
      <Paper p="sm" withBorder>
        <Text c="dimmed" ta="center" size="sm">
          Save the case first to add notes
        </Text>
      </Paper>
    );
  }

  const content = (
    <Stack gap="md">
      {!embedded &&
        (canCollapse ? (
          <UnstyledButton
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ width: "100%" }}
          >
            <Group justify="space-between">
              <Group gap="xs">
                <IconNote size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm" fw={500}>
                  Notes
                </Text>
                <Badge size="sm" variant="light" color="blue">
                  {notes.length}
                </Badge>
              </Group>
              {effectiveExpanded ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </Group>
          </UnstyledButton>
        ) : (
          <Group gap="xs">
            <IconNote size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={500}>
              Notes
            </Text>
          </Group>
        ))}

      {/* Collapsed preview - show latest note */}
      {!effectiveExpanded && latestNote && (
        <UnstyledButton
          onClick={() => setIsExpanded(true)}
          style={{ width: "100%" }}
        >
          <Card withBorder p="xs" bg="blue.0">
            <Text size="xs" lineClamp={2} c="dimmed">
              <Text span fw={500} c="dark">
                Latest:
              </Text>{" "}
              {latestNote.noteText}
            </Text>
          </Card>
        </UnstyledButton>
      )}

      <Collapse in={effectiveExpanded}>
        <Stack gap="md">
          {!isAdding && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setIsAdding(true)}
            >
              Add Note
            </Button>
          )}

          {isAdding && (
            <Stack gap="sm">
              <RichTextDiagnosis
                value={newNote}
                onChange={setNewNote}
                placeholder="Enter note..."
                minHeight={80}
              />
              <Group justify="flex-end" gap="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    setIsAdding(false);
                    setNewNote("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  onClick={handleAddNote}
                  loading={createNoteMutation.isPending}
                  disabled={!newNote.replace(/<[^>]*>/g, "").trim()}
                >
                  Save Note
                </Button>
              </Group>
            </Stack>
          )}

          {isLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : notes.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center">
              No notes yet
            </Text>
          ) : (
            <Stack gap="xs">
              {notes.map((note: CaseNote) => (
                <Card key={note.id} withBorder p="xs" bg="gray.0">
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <Stack gap={4} style={{ flex: 1 }}>
                      <div
                        style={{
                          wordBreak: "break-word",
                          fontSize: "var(--mantine-font-size-sm)",
                        }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: note.noteText }}
                      />
                      <Text size="xs" c="dimmed">
                        {note.createdBy?.fullName || "Unknown"} •{" "}
                        {formatDate(note.createdAt)}
                      </Text>
                    </Stack>
                    <Tooltip label="Delete note">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => deleteNoteMutation.mutate(note.id)}
                        loading={deleteNoteMutation.isPending}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );

  if (embedded) return content;
  return (
    <Paper p="sm" withBorder>
      {content}
    </Paper>
  );
}
